import * as React from 'react';
import { CreateTask } from './CreateTask';
import { PrimaryButton } from '@fluentui/react';
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
} from "@fluentui/react-components";import { IAbo } from '../../api/model/IAbo';
import * as ss from 'string-similarity';
import { IProject } from '../../api/model/IProject';
import { Project } from '../../api/Project';
import { Configuration } from '../../api/Configuration';
import { AbsenceList } from './AbsenceList';
import { AllTasksList } from './AllTasksList';
import { startOfMonth, endOfMonth } from '../../utils/time.utils'
import { Field, webDarkTheme, webLightTheme, } from "@fluentui/react-components";
import { SearchBox } from "@fluentui/react-components";
import { Stack } from "@fluentui/react";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { useGlobalState } from '../../service/globalState.service';



const ScrollList: React.FC<{ offset: number }> = ({ offset, children }) => (
  <>
    <div style={{ height: `calc(100vh - ${offset}px)`, overflow: 'auto' }}>
      {children}
    </div>
  </>
)

export const Tabs: React.FC = () => {
  const [hidden, setHidden] = React.useState(true);
  const [filter, setFilter] = React.useState<string>();
  const [itemStartDate, setDate] = React.useState<Date>();
  const [filterStart, setFilterStart] = React.useState<Date>();
  const [filterEnd, setFilterEnd] = React.useState<Date>();
  const [projects, setProjects] = React.useState<IProject[]>([]);
  const [{ darkTheme, showAllTasks }] = useGlobalState('settings');
  const [ message ] = useGlobalState('message');
  const theme = darkTheme ? webDarkTheme : webLightTheme;
    const background = darkTheme ? theme.colorBrandBackground : 'white';
  const [absenceProjectFilter, setAbsenceProjectFilter] = React.useState('');

  React.useEffect(() => {
    fetchConfiguration();
    fetchProjects();
    parseStart();

    if (!absenceProjectFilter) {
      parseSubject();
    }
  }, [])

  const fetchConfiguration = async () => {
    const configEntries = await Configuration.getAll();
    const absenceTaskSendOnly = configEntries.find(c => c.Id === 'AbsenceTaskSendOnly');
    if (absenceTaskSendOnly?.Value !== 'True') {
      return;
    }

    const absenceProjectFilter = configEntries.find(c => c.Id === 'AbsenceFilterProjNameLike');
    if (absenceProjectFilter?.Value) {
      setAbsenceProjectFilter(absenceProjectFilter.Value);
      setFilter('');
    }
  }

  const fetchProjects = async () => {
    const projects = await Project.getAll();
    setProjects(projects);
  }

  const parseSubject = () => {
    const { item } = Office.context.mailbox;
    item.subject.getAsync(r => {
      if (r.value) setFilter(r.value);
      else setFilter('');
    })
  }

  const parseStart = async (): Promise<Date> => {
    const { item } = Office.context.mailbox;
    return new Promise((resolve) => {
      item.start.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
          setDate(result.value);
        } else {
          setDate(new Date());
        }
      });
    });
  };
  
  const commonSearch = () => (<>
    <SearchBox
      value={filter || ''}
      onChange={(e, v) => setFilter(v.value)}
      placeholder='Search tasks'
    />
  </>);

  const dateRangePicker = () => (
    <>
    <div style={{ display: 'inline-flex', gap: 12, marginBottom: 16, marginTop: 8 }}>          
      <Stack horizontal tokens={{ childrenGap: 36 }} style={{ overflow: 'clip' }}> 
      <Stack.Item grow={1} styles={{ root: { maxWidth: '35%' } }}>
        <Field label="from" required>  
        <div style={{ maxWidth: 150 }}>
          <DatePicker
          size='medium'
          style={{ maxWidth: 150, width: '100%' }}
          value={filterStart}
          onSelectDate={setFilterStart}
          placeholder={showAllTasks ? "Select from Date" : startOfMonth(itemStartDate).toDateString()}
          initialPickerDate={startOfMonth(itemStartDate)}
          />
        </div>
        </Field>
      </Stack.Item>
      <Stack.Item grow={1} styles={{ root: { maxWidth: '35%' } }}>
        <Field label="to" required> 
        <div style={{ maxWidth: 150 }}>
          <DatePicker
          size='medium'
          style={{ maxWidth: 150, width: '100%' }}
          value={filterEnd}
          onSelectDate={setFilterEnd}
          placeholder={showAllTasks ? "Select to Date" : endOfMonth(itemStartDate).toDateString()}
          initialPickerDate={endOfMonth(itemStartDate)}
          />
        </div>
        </Field>
      </Stack.Item>
      </Stack>
    </div>

  </>);

  const aboFilter = (abos: IAbo[]): IAbo[] => {
    if (!abos || abos.length === 0 || !filter) return abos;
  
    const lowerCaseFilter = filter.toLowerCase();
  
    const projectNames = abos.map(a => a.ProjectName.toLowerCase());
    const taskNames = abos.map(a => a.TaskName.toLowerCase());
  
    const { ratings: projectRatings } = ss.findBestMatch(lowerCaseFilter, projectNames);
    const { ratings: taskRatings } = ss.findBestMatch(lowerCaseFilter, taskNames);
  
    let ratedAbos;
    let ratingThreshold = 0.25;

    ratedAbos = abos.map((a, i) => {
      const projectName = projectNames[i];
      const taskName = taskNames[i];

      const projectBonus = projectName.includes(lowerCaseFilter) ? 0.5 : 0;
      const taskBonus = taskName.includes(lowerCaseFilter) ? 0.5 : 0;

      const combinedRating = projectRatings[i].rating + projectBonus + taskRatings[i].rating + taskBonus;

      return { abo: a, combinedRating };
    }).filter(({ combinedRating }) => combinedRating > ratingThreshold);
    
    ratedAbos.sort((a, b) => b.combinedRating - a.combinedRating);
    return ratedAbos.map(({ abo }) => abo);
  }

  const dismiss = () => setHidden(true);
  return (
    <>
      {absenceProjectFilter ? (
        <>
          {commonSearch()}
          <ScrollList offset={ message == null ? 245 : 295 }>
            <AbsenceList filter={aboFilter} absenceProjectFilter={absenceProjectFilter} />
          </ScrollList>
        </>
      ) : (
        <>
        {commonSearch()}
        {itemStartDate && dateRangePicker()}
        <ScrollList offset={ message == null ? 245 : 295 }>
          <AllTasksList 
            filter={aboFilter}
            filterStart={filterStart}
            filterEnd={filterEnd}
          />
        </ScrollList>
        <div
          style={{
            position: 'fixed',
            background: theme.colorNeutralBackground1,
            bottom: 0,
            left: 0,  
            right: 0,
            height: 60,
            borderTop: '1.5px solid lightgray',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}>
          <div style={{ background: theme.colorNeutralBackground1, paddingRight: 20 }}>
            <PrimaryButton
              text='Create Task'
              onClick={() => setHidden(false)}
            />
          </div>
        </div>
      </>
      )}
      <Dialog
        open={!hidden}
        onOpenChange={(_, data) => setHidden(!data.open)}
      >
        <DialogSurface style={{ width: 300, minHeight: 400 }}>
          <DialogTitle>Create new task</DialogTitle>
          <DialogBody>
            <CreateTask onCreateSuccess={dismiss} projects={projects} />
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}